import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchUnassignedItemsForInvoice } from '@/redux/thunk/v2/unassignedItemsThunk';
import {
  UnassignedItem,
  UnassignedItemsBlock,
  UnassignedSection,
} from '@/api/v2/unassignedItemsForInvoice';
import {
  UnassignedItemPayload,
  UnassignedSectionPayload,
} from '@/api/v2/generateInvoice';

/**
 * Picker state for job line items that belong to no phase.
 *
 * Selection is keyed on ITEM uuid only. Ticking a section is a convenience that toggles the items beneath
 * it so the two levels cannot drift. The API takes a flat unassigned_items[] of item uuids.
 */

export interface UnassignedItemsState {
  block: UnassignedItemsBlock | null;
  // Item uuids the user has ticked.
  selectedItems: string[];
  loading: boolean;
  error: string | null;
}

const initialState: UnassignedItemsState = {
  block: null,
  selectedItems: [],
  loading: false,
  error: null,
};

// Tree Helpers

const collectItems = (section: UnassignedSection): UnassignedItem[] => [
  ...(section.products || []),
  ...(section.sections || []).flatMap(collectItems),
];

const findSection = (
  sections: UnassignedSection[],
  sectionUuid: string,
): UnassignedSection | null => {
  for (const section of sections) {
    if (section.uuid === sectionUuid) return section;
    const found = findSection(section.sections || [], sectionUuid);
    if (found) return found;
  }
  return null;
};

// Items a user is allowed to tick: everything not already billed on an active invoice.
const selectableItems = (section: UnassignedSection): UnassignedItem[] =>
  collectItems(section).filter((item) => !item.is_invoiced);

const allBlockItems = (block: UnassignedItemsBlock | null): UnassignedItem[] =>
  block ? (block.sections || []).flatMap(collectItems) : [];

// Slice
const unassignedItemsSlice = createSlice({
  name: 'v2/unassignedItems',
  initialState,
  reducers: {
    // Tick or untick a single line item.
    toggleItemSelection: (
      state,
      action: PayloadAction<{ itemUuid: string }>,
    ) => {
      const { itemUuid } = action.payload;
      const index = state.selectedItems.indexOf(itemUuid);

      if (index > -1) {
        state.selectedItems.splice(index, 1);
      } else {
        state.selectedItems.push(itemUuid);
      }
    },

    /**
     * Tick or untick a whole section, including everything in its subsections.
     *
     * Already-invoiced items are excluded, so a section holding only billed items has nothing to toggle
     * and its control renders disabled.
     */
    toggleSectionSelection: (
      state,
      action: PayloadAction<{ sectionUuid: string }>,
    ) => {
      const { sectionUuid } = action.payload;
      if (!state.block) return;

      const section = findSection(state.block.sections || [], sectionUuid);
      if (!section) return;

      const uuids = selectableItems(section).map((item) => item.uuid);
      if (uuids.length === 0) return;

      const allSelected = uuids.every((uuid) =>
        state.selectedItems.includes(uuid),
      );

      if (allSelected) {
        state.selectedItems = state.selectedItems.filter(
          (uuid) => !uuids.includes(uuid),
        );
      } else {
        const missing = uuids.filter(
          (uuid) => !state.selectedItems.includes(uuid),
        );
        state.selectedItems.push(...missing);
      }
    },

    /**
     * Rename a section for this invoice only.
     */
    updateSectionField: (
      state,
      action: PayloadAction<{
        sectionUuid: string;
        field: 'title' | 'description';
        value: string;
      }>,
    ) => {
      const { sectionUuid, field, value } = action.payload;
      if (!state.block) return;

      const section = findSection(state.block.sections || [], sectionUuid);
      if (!section) return;

      section[field] = value;
    },

    /**
     * Edit a line item's numbers for this invoice only.
     */
    updateItemField: (
      state,
      action: PayloadAction<{
        itemUuid: string;
        field:
          | 'quantity'
          | 'unit_cost'
          | 'unit_price'
          | 'markup'
          | 'tax'
          | 'discount';
        value: number;
      }>,
    ) => {
      const { itemUuid, field, value } = action.payload;
      if (!state.block) return;

      const visit = (sections: UnassignedSection[]): boolean => {
        for (const section of sections) {
          const item = (section.products || []).find(
            (product) => product.uuid === itemUuid,
          );
          if (item) {
            item[field] = value;
            return true;
          }
          if (visit(section.sections || [])) return true;
        }
        return false;
      };

      visit(state.block.sections || []);
    },

    clearSelections: (state) => {
      state.selectedItems = [];
    },

    clearUnassignedItems: (state) => {
      state.block = null;
      state.selectedItems = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnassignedItemsForInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnassignedItemsForInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.block = action.payload?.data ?? null;

        // Drop selections for items the refetch no longer offers, or that have since been billed.
        const stillSelectable = allBlockItems(state.block)
          .filter((item) => !item.is_invoiced)
          .map((item) => item.uuid);
        state.selectedItems = state.selectedItems.filter((uuid) =>
          stillSelectable.includes(uuid),
        );
      })
      .addCase(fetchUnassignedItemsForInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch unassigned items';
      });
  },
});

export const {
  toggleItemSelection,
  toggleSectionSelection,
  updateSectionField,
  updateItemField,
  clearSelections,
  clearUnassignedItems,
} = unassignedItemsSlice.actions;

// Selectors

type UnassignedItemsRoot = { unassignedItemsV2: UnassignedItemsState };

export const selectUnassignedBlock = (state: UnassignedItemsRoot) =>
  state.unassignedItemsV2.block;

export const selectUnassignedItemsLoading = (state: UnassignedItemsRoot) =>
  state.unassignedItemsV2.loading;

export const selectUnassignedItemsError = (state: UnassignedItemsRoot) =>
  state.unassignedItemsV2.error;

export const selectUnassignedSelectedItems = (state: UnassignedItemsRoot) =>
  state.unassignedItemsV2.selectedItems;

export const selectIsUnassignedItemSelected = (
  state: UnassignedItemsRoot,
  itemUuid: string,
): boolean => state.unassignedItemsV2.selectedItems.includes(itemUuid);

/**
 * Tri-state for a section's own checkbox, plus whether it can be interacted with at all.
 */
export const selectSectionSelectionState = (
  state: UnassignedItemsRoot,
  sectionUuid: string,
): { checked: boolean; indeterminate: boolean; disabled: boolean } => {
  const block = state.unassignedItemsV2.block;
  const none = { checked: false, indeterminate: false, disabled: true };
  if (!block) return none;

  const section = findSection(block.sections || [], sectionUuid);
  if (!section) return none;

  const uuids = selectableItems(section).map((item) => item.uuid);
  if (uuids.length === 0) return none;

  const selectedCount = uuids.filter((uuid) =>
    state.unassignedItemsV2.selectedItems.includes(uuid),
  ).length;

  return {
    checked: selectedCount === uuids.length,
    indeterminate: selectedCount > 0 && selectedCount < uuids.length,
    disabled: false,
  };
};

export const buildUnassignedItemsPayload = (
  block: UnassignedItemsBlock | null,
  selectedItems: string[],
): UnassignedItemPayload[] => {
  return allBlockItems(block)
    .filter((item) => selectedItems.includes(item.uuid))
    .map((item) => ({
      uuid: item.uuid,
      quantity: item.quantity,
      unit_cost: item.unit_cost,
      unit_price: item.unit_price,
      markup: item.markup,
      tax: item.tax,
      discount: item.discount,
    }));
};

/**
 * A phase-shaped copy of the block holding only the ticked items, for the totals preview.
 *
 * It goes through the same convertInvoicePhasesToSections the phase cards use, so the preview and the
 * saved invoice are computed from one shape and cannot disagree. Sections with nothing ticked are pruned,
 * and the whole thing returns null when nothing is selected, so no empty block reaches the calculation.
 */
export const buildUnassignedFinanceBlock = (
  block: UnassignedItemsBlock | null,
  selectedItems: string[],
): UnassignedItemsBlock | null => {
  if (!block || selectedItems.length === 0) return null;

  const prune = (section: UnassignedSection): UnassignedSection | null => {
    const products = (section.products || []).filter((item) =>
      selectedItems.includes(item.uuid),
    );
    const sections = (section.sections || [])
      .map(prune)
      .filter((child): child is UnassignedSection => child !== null);

    if (products.length === 0 && sections.length === 0) return null;

    return { ...section, products, sections };
  };

  const sections = (block.sections || [])
    .map(prune)
    .filter((section): section is UnassignedSection => section !== null);

  if (sections.length === 0) return null;

  return { ...block, sections };
};

/**
 * The unassigned_sections[] payload: title and description for every section actually being billed.
 *
 * Derived from the pruned finance block rather than the whole tree, so a section the user renamed but then
 * unticked does not write a row for an invoice that never included it.
 */
export const buildUnassignedSectionsPayload = (
  block: UnassignedItemsBlock | null,
  selectedItems: string[],
): UnassignedSectionPayload[] => {
  const pruned = buildUnassignedFinanceBlock(block, selectedItems);
  if (!pruned) return [];

  const out: UnassignedSectionPayload[] = [];
  const walk = (sections: UnassignedSection[]) => {
    sections.forEach((section) => {
      out.push({
        uuid: section.uuid,
        title: section.title ?? '',
        description: section.description ?? '',
      });
      walk(section.sections || []);
    });
  };
  walk(pruned.sections || []);

  return out;
};

/**
 * String identity of the current selection.
 *
 * Values are included, not just uuids, so the totals still refresh if a line item becomes editable in the picker later.
 */
export const buildUnassignedSelectionSignature = (
  payload: UnassignedItemPayload[],
): string =>
  payload
    .map(
      (item) =>
        `${item.uuid}-${item.quantity}-${item.unit_cost}-${item.unit_price}-${item.markup}-${item.tax}-${item.discount}`,
    )
    .sort()
    .join('|');

export default unassignedItemsSlice.reducer;
